import { DestroyRef, WritableSignal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { EMPTY, Observable } from 'rxjs';
import {
    catchError,
    debounceTime,
    distinctUntilChanged,
    filter,
    share,
    switchMap,
} from 'rxjs/operators';

/**
 * Reusable search handler that listens to a signal and triggers
 * an Observable factory whenever the value changes, cancelling any in-flight
 * requests when a new value is emitted.
 *
 * @remarks
 * Use the `onEmpty` callback to handle the case when the value is considered empty.
 *
 * @template T - The type of data returned by the observable factory
 * @template S - The type of the signal value
 * @param {WritableSignal<S>} signal - Signal holding the current value
 * @param {(value: S) => Observable<T>} observableFactory - Factory function that accepts the value and returns an Observable (e.g. an API call)
 * @param {DestroyRef} destroyRef - Angular's DestroyRef to auto-unsubscribe on component destroy
 * @param {Object} [options] - Optional configurations
 * @param {number} [options.debounceTime] - Optional debounce time in ms (defaults to 300ms)
 * @param {(value: S) => boolean} [options.isEmpty] - Optional function to determine if value is empty (defaults to string length check)
 * @param {Object} [callbacks] - Optional callbacks for handling emitted data and errors
 * @returns {void}
 */
export function searchHandler<T, S = string>(
    signal: WritableSignal<S>,
    observableFactory: (value: S) => Observable<T>,
    destroyRef: DestroyRef,
    callbacks?: {
        next?: (data: T) => void;
        error?: (error: unknown) => void;
        onEmpty?: () => void;
    },
    options?: {
        debounceTime?: number;
        isEmpty?: (value: S) => boolean;
    },
): void {
    const defaultIsEmpty = (val: any) => {
        if (typeof val === 'string') return val.length === 0;
        if (val === null || val === undefined) return true;
        return false;
    };

    const isEmptyFn = options?.isEmpty || defaultIsEmpty;
    const debounce = options?.debounceTime ?? 300;

    const source$ = toObservable(signal).pipe(distinctUntilChanged(), share());

    // Main search flow
    source$
        .pipe(
            filter((value) => !isEmptyFn(value)),
            debounceTime(debounce),
            switchMap((value) =>
                observableFactory(value).pipe(
                    catchError((error) => {
                        callbacks?.error?.(error);
                        return EMPTY;
                    }),
                ),
            ),
            takeUntilDestroyed(destroyRef),
        )
        .subscribe({
            next: (data) => callbacks?.next?.(data),
        });

    // Empty state flow
    source$
        .pipe(
            filter((value) => isEmptyFn(value)),
            takeUntilDestroyed(destroyRef),
        )
        .subscribe(() => {
            callbacks?.onEmpty?.();
        });
}
